import mqtt from 'mqtt'
import _ from 'lodash';
import React, {useState, useEffect} from 'react';
import recursiveParse from '../utils/recursiveParse';

let options = {
  username: "HCActiveMQ",
  password: process.env.REACT_APP_AMQ_PASSWORD, //Password and endpoint in .env file for security reasons
  //clientId: 'mqttLambda_HC', //Not included as it causes connection to fail for some reason
  protocolId: 'MQTT',
  protocolVersion: 4,
  // protocolId: 'MQIsdp',
  //protocolVersion: 3,
  debug: false,
  keepalive: (5*60)-5, //5 minutes minus 5 seconds to avoid timeout
  clean: true,
  reconnectPeriod: 5000,
  queueQoSZero: true,
}

let debugFilters = ["silentFailure"]; //Keys for which debug logs are allowed to appear


//Client is created on page load and closed on page unload. This way, a single client subscribes to all topics
//Previously, every topic got its own client, which was resource intensive.
//See also BUG-1493
let mqEndpoint = process.env.REACT_APP_AMQ_ENDPOINT;
let client = mqtt.connect(mqEndpoint, options);

window.addEventListener("unload", function (){
  console.log("AMQ calling closeClient");
  if (client !== null && client.end !== undefined && typeof client.end === 'function') client.end();
  return null;
})

let connections = {};
let listenersInitialized = false;
let offlineConnections = [];

function identifierToKey(identifier){
  if (identifier === undefined || identifier === null) return "";
  if (typeof identifier === "object") {
    try {
      return JSON.stringify(identifier);
    } catch (e) {
      return String(identifier);
    }
  }
  return String(identifier);
}

function identifiersMatch(leftIdentifier, rightIdentifier){
  return identifierToKey(leftIdentifier) === identifierToKey(rightIdentifier);
}

function debugLog(filterKey, ...args) {
  if (debugFilters.indexOf(filterKey) !== -1){
    console.debug(`[AMQ DEBUG] [${filterKey}]`, ...args);
  }
}

function activityLog(...args) {
  console.info("[AMQ INFO]", ...args);
}

function errorLog(...args) {
  console.error("[AMQ ERROR]", ...args);
}

function parseMessageObject(message){

  try {
    debugLog("parseMessageObject", "received message:", message);
    let parsedMessage = recursiveParse(message);
    debugLog("parseMessageObject", "parsed message:", parsedMessage);
    return parsedMessage;
  } catch (e) {
    debugLog("parseMessageObject", "Failed to parse message:", message, "Error:", e);
    return null;
  }
}

function extractMessageIdentifier(message){
  let messageObject = parseMessageObject(message);
  if (!messageObject){ 
    errorLog("Failed to extract message identifier, message object is null");
    return undefined;
  }
  // If the message object contains a nested message, clone it to avoid mutating the original
  if (messageObject.message && messageObject.message.identifier) messageObject = _.cloneDeep(messageObject.message);
  debugLog("extractMessageIdentifier", "received message:", messageObject);

  if (messageObject.identifier !== undefined) {
    debugLog("extractMessageIdentifier", "found identifier in messageObject:", messageObject.identifier);
    return messageObject.identifier;
  }
  if (messageObject.payload && messageObject.payload.identifier !== undefined) {
    debugLog("extractMessageIdentifier", "found identifier in messageObject.payload:", messageObject.payload.identifier);
    return messageObject.payload.identifier;
  }
  if (messageObject.data && messageObject.data.identifier !== undefined) {
    debugLog("extractMessageIdentifier", "found identifier in messageObject.data:", messageObject.data.identifier);
    return messageObject.data.identifier;
  }
  
  debugLog("extractMessageIdentifier", "message did not include identifier:", message);
  return undefined;
}

function initializeClientListeners(){
  if (listenersInitialized) return;
  listenersInitialized = true;

  client.on('offline', async(packet) => {
    activityLog("quickSubscribe offline");
    // According to documentation in this case the client is the one who has gone offline,
    // Ask to reload! or try to reconnect
    //client.reconnect();
    //client.end();
    for (var i = 0; i < offlineConnections.length; i++) {
      let currentOfflineCallback = offlineConnections[i].callback;
      if (currentOfflineCallback && typeof currentOfflineCallback === "function") currentOfflineCallback();
    }
  });

  client.on('message', async (topic, message) => {
    //console.log("AMQ", "quickSubscribe recieved raw message", message, " from topic " + topic)
    //let message = new TextDecoder("utf-8").decode(message_raw)

    message = await parseUint8Array(message);

    message = parseMessageObject(message);

    activityLog("Received message", message, "from topic", topic);
    /*debugLog("quickSubscribe", "message.payload:", message.payload);
    debugLog("quickSubscribe", "message instanceOf uint8Array:", message instanceof Uint8Array);*/

    debugLog("quickSubscribe", "Connections:", connections);

    if (_.isEmpty(connections[topic])) {
      debugLog("silentFailure", "No registered connections for topic", topic, "Skipping dispatch");
      return;
    }

    let messageIdentifier = extractMessageIdentifier(message);
    if (messageIdentifier === undefined) {
      activityLog("Message did not include identifier. Skipping dispatch for topic", topic);
      return;
    }

    activityLog("Pre-dispatch state:", {
      topic,
      messageIdentifier,
      registeredConnections: connections[topic]
    });

    for (var i = 0; i < connections[topic].length; i++){
      let {callback, identifier} = connections[topic][i];

      debugLog("silentFailure", "Checking connection:", {
        checkingIdentifier: identifier,
        messageIdentifier,
        identifiersMatch: identifiersMatch(identifier, messageIdentifier)
      });

      if (identifiersMatch(identifier, messageIdentifier) && callback && typeof callback == "function") {
        activityLog("Dispatching message", message, "to callback for topic", topic, "with identifier", identifier);
        callback(message.message);
        break;
      } else if (identifiersMatch(identifier, messageIdentifier) && !callback) {
        errorLog("Skipping dispatch for topic", topic, "because callback is missing for identifier", identifier);
        break;
      } else if (identifiersMatch(identifier, messageIdentifier) && typeof callback !== "function") {
        errorLog("Skipping dispatch for topic", topic, "because callback is not a function for identifier", identifier);
        break;
      }
      //Don't add an else here because that just means the loop continued to search
    }
  })

  client.on('reconnect', (/*No passed args*/) => {
    activityLog("Reconnecting");
  });

  client.on('error', (err) => {
    errorLog("Error emitted:", err);
    if (err==='ECONNRESET') {
      //client.reconnect();
    }
  })

  client.on('close', (originInfo) => {
    if (originInfo === undefined) {
      activityLog("Close event emitted without origin info");
    } else {
      activityLog("Close event emitted", originInfo);
    }
  })
}

async function quickSubscribe(originName = "[caller unknown]", topic = false, identifier = false, handleMessageCallback = false, offlineCallback = false){

  let subscribeToTopic = (passedTopic, passedIdentifier, passedCallback) => {
    
    debugLog("silentFailure", "Adding subscription: Topic:", passedTopic, "Identifier:", passedIdentifier);
    
    let addOrUpdateConnection = () => {
      if (connections[passedTopic]){
        let existingIndex = -1;
        for (var i = 0; i < connections[passedTopic].length; i++) {
          if (identifiersMatch(connections[passedTopic][i].identifier, passedIdentifier)) {
            existingIndex = i;
            break;
          }
        }

        if (existingIndex >= 0) {
          connections[passedTopic][existingIndex].callback = passedCallback;
        } else {
          connections[passedTopic].push({callback: passedCallback, identifier: passedIdentifier});
        }
      } else {
        connections[passedTopic] = [{callback: passedCallback, identifier: passedIdentifier}];
      }

      if (offlineCallback && typeof offlineCallback === "function") {
        let existingOfflineIndex = -1;
        for (var j = 0; j < offlineConnections.length; j++) {
          if (identifiersMatch(offlineConnections[j].identifier, passedIdentifier)) {
            existingOfflineIndex = j;
            break;
          }
        }

        if (existingOfflineIndex >= 0) {
          offlineConnections[existingOfflineIndex].callback = offlineCallback;
        } else {
          offlineConnections.push({identifier: passedIdentifier, callback: offlineCallback});
        }
      }

      activityLog("Subscription success\nOrigin: " + originName + "\nTopic: " + passedTopic, "Identifier: ",passedIdentifier);
     }

     if (connections[passedTopic]) {
      addOrUpdateConnection();
      return;
     }

     client.subscribe(passedTopic, (err)=>{
      if (err){
        errorLog("Error from origin "+originName+":", err);
      } else {
        addOrUpdateConnection();
      }
    });
  }

  let badTopicOrCallbackMsg = (passedTopic, identifier, passedCallback) => {
    activityLog("Invalid topic", passedTopic, typeof passedTopic, "and/or message callback", passedCallback, typeof passedCallback, "from origin", originName, "with identifier", identifier)
  }

  let validTopic = (passedTopic) => {
    return (passedTopic && typeof passedTopic == "string");
  }

  let validCallback = (passedCallback) => {
    return (passedCallback && typeof passedCallback == "function")
  }



  if (topic == false) return;

  initializeClientListeners();

  if (Array.isArray(topic) && Array.isArray(handleMessageCallback)){
    //console.log("AMQ Calling quickSubscribe with topic and callbacks in array format")

    for (var i = 0; i < topic.length; i++){
      let currentTopic = topic[i];
      let currentCallback = handleMessageCallback[i];

      if (validTopic(currentTopic) && validCallback(currentCallback)){
        subscribeToTopic(currentTopic, identifier, currentCallback)
      } else {
        badTopicOrCallbackMsg(currentTopic, identifier, currentCallback);
      }
    }

  } else if (typeof topic == "string" && typeof handleMessageCallback == "function") {

   // console.log("AMQ calling quickSubscribe with topic and callbacks in string/function format")

    subscribeToTopic(topic, identifier, handleMessageCallback);
  } else {
    badTopicOrCallbackMsg(topic, identifier, handleMessageCallback);
  }

  return client;
}

async function parseUint8Array (message){
  if (message instanceof Uint8Array){
    //console.log("AMQ", "Message being parsed from Uint8Array:", message);
    var plainMessage = "";
    for (var i = 0; i < message.length; i++) {
      plainMessage += String.fromCharCode(parseInt(message[i]));
    }
    //console.log("AMQ","plainMessage:", plainMessage);

    return plainMessage;
  } else {
    return message;
  }
}


async function closeSubscription(originName = "[caller unknown]", identifier, topic){
  if (client !== null && client.unsubscribe !== undefined && typeof client.unsubscribe === 'function' && topic !== undefined) {
    //client.unsubscribe(topic);
    if (connections[topic]) {
      // Remove the specific callback for this origin/identifier
      connections[topic] = connections[topic].filter(item => {
        return !identifiersMatch(item.identifier, identifier)
      });
      // If no more callbacks for this topic, clean up the array
      if (connections[topic].length === 0) {
        delete connections[topic];
      }
    }

    activityLog("Closing subscription\nOrigin: ",originName,"\nTopic: ",topic,"\nIdentifier: ",identifier,"\nRemaining connections:", connections);

    offlineConnections = offlineConnections.filter(item => !identifiersMatch(item.identifier, identifier));
  };
  return client;
}

//Hook implemented for BUG-1493
//This helps keep logic consistent among components

function usePublishSubscribe ({origin, topic, identifier, callback, onOfflineCallback}) {

  useEffect(() => {
    debugLog("usePublishSubscribe", "Calling useEffect from usePublishSubscribe from origin ", origin, "and identifier", identifier)
     initializePS();
     return () => unmountPS()
  }, [])

  let initializePS = () => {
    debugLog("usePublishSubscribe", "Calling initializeSubscription from usePublishSubscribe from origin ", origin, "and identifier", identifier, "with quickSubscribe", quickSubscribe)

    quickSubscribe(
      origin,
      topic,
      identifier,
      (message) => callback(message),
      onOfflineCallback
    );
  }

  let unmountPS = () => {
    debugLog("usePublishSubscribe", "Calling unmountPS from usePublishSubscribe from origin ", origin, "and identifier", identifier)
    closeSubscription(origin, identifier, topic);
  }

  return {unmountPS}

}

export const PublishSubscribe = {
	quickSubscribe, closeSubscription, usePublishSubscribe
}
